/**
 * ADR-036 — the access model, as the SPA sees it.
 *
 * The backend's `organization.AccessAuthorities` turns a membership row into exactly
 * six authority names and puts them in the JWT and in `LoginResponse.MemberSummary`;
 * `authStore.teamMember.roles` is that list, verbatim. This file is its mirror — the
 * one place the SPA writes those six names down, so a route guard, a nav row and a
 * feature flag cannot drift apart the way the nine-value role enum let them.
 *
 * ```
 * ROLE_OWNER | ROLE_ADMIN | ROLE_CREW | ROLE_BOOKKEEPER   exactly one per membership
 * CAN_OPEN_FINANCE                                        OWNER || BOOKKEEPER || (ADMIN && switch)
 * SCOPE_ASSIGNED_ONLY                                  CREW && switch
 * ```
 *
 * **The two switches are deliberately spelled without a `ROLE_` prefix**, exactly as on
 * the server, so that no `hasAnyRole`-shaped list — here `allowedRoles`, there
 * `@PreAuthorize` — can absorb one by accident.
 *
 * **Craft tags grant nothing.** Photographer, Videographer, Editor, Culler, Retoucher…
 * are free-form staffing labels a studio types; they never appear in a guard.
 *
 * This is the UX layer, not the security boundary: it decides whether a screen renders
 * or an affordance is offered. The boundary is the backend `@PreAuthorize` set, proven
 * by `RoleAccessMatrixIntegrationTest`.
 */

/** The four access levels of ADR-036 axis A. Exactly one per membership. */
export const ACCESS_LEVELS = ['OWNER', 'ADMIN', 'CREW', 'BOOKKEEPER'] as const
export type AccessLevel = (typeof ACCESS_LEVELS)[number]

/**
 * ADR-036 axis D — and, since 2026-09-07, the ONLY column that carries it: the server
 * dropped `is_contractor` and derives it from this (changeset 369).
 *
 * EXTERNAL is a contractor: no login, no seat, and no access settings at all — no level,
 * no switches, nothing to scope, because there is nobody to sign in. IN_HOUSE is a team
 * member who signs in and occupies a seat. Seats are counted off this value. There is no
 * conversion in either direction; the server refuses one.
 */
export const ENGAGEMENTS = ['IN_HOUSE', 'EXTERNAL'] as const
export type Engagement = (typeof ENGAGEMENTS)[number]

/**
 * The six authority names, spelled once. Levels carry the `ROLE_` prefix; the two
 * switches deliberately do not.
 */
export const AUTH = {
  OWNER: 'ROLE_OWNER',
  ADMIN: 'ROLE_ADMIN',
  CREW: 'ROLE_CREW',
  BOOKKEEPER: 'ROLE_BOOKKEEPER',
  CAN_OPEN_FINANCE: 'CAN_OPEN_FINANCE',
  SCOPE_ASSIGNED_ONLY: 'SCOPE_ASSIGNED_ONLY',
} as const

/**
 * Authorities arrive `ROLE_`-prefixed from the server and are sometimes written bare
 * in a guard list. Normalising both sides keeps a missing prefix from silently denying.
 * A switch name has no prefix to strip, so it survives untouched.
 */
export const normalizeAuthority = (a: string) => (a.startsWith('ROLE_') ? a.slice(5) : a)

/** Whether `mine` contains `wanted`, prefix-insensitively. */
export function hasAuthority(mine: readonly string[] | null | undefined, wanted: string): boolean {
  const target = normalizeAuthority(wanted)
  return (mine ?? []).some((a) => normalizeAuthority(a) === target)
}

/** Whether `mine` contains at least one of `wanted`. `null` means "no restriction". */
export function hasAnyAuthority(
  mine: readonly string[] | null | undefined,
  wanted: readonly string[] | null | undefined,
): boolean {
  if (!wanted) return true
  return wanted.some((w) => hasAuthority(mine, w))
}

/**
 * The level this authority set carries, or null for a session that has none (an
 * identity-only session, or a token minted before this model existed).
 */
export function accessLevelOf(mine: readonly string[] | null | undefined): AccessLevel | null {
  for (const level of ACCESS_LEVELS) {
    if (hasAuthority(mine, level)) return level
  }
  return null
}

export const isOwner = (mine: readonly string[] | null | undefined) => hasAuthority(mine, AUTH.OWNER)
export const isAdmin = (mine: readonly string[] | null | undefined) => hasAuthority(mine, AUTH.ADMIN)
export const isCrew = (mine: readonly string[] | null | undefined) => hasAuthority(mine, AUTH.CREW)
export const isBookkeeper = (mine: readonly string[] | null | undefined) => hasAuthority(mine, AUTH.BOOKKEEPER)

/** Owner or Admin — the two levels that run the studio day to day. */
export const runsTheStudio = (mine: readonly string[] | null | undefined) =>
  isOwner(mine) || isAdmin(mine)

/**
 * May SEND a contract, and so may read its client URL back (`GET /contracts/{id}/share-link`).
 * Mirrors the backend's one shared gate, `ContractAccess.MAY_SEND` = hasAnyRole(OWNER, ADMIN,
 * CREW). The URL resolves AND accepts, so it is exactly as trusted as sending (IN-216 D-02);
 * a Bookkeeper reads contracts but gets 403 there, so the control must not be drawn for them.
 */
export const canSendDocuments = (mine: readonly string[] | null | undefined) =>
  isOwner(mine) || isAdmin(mine) || isCrew(mine)

/**
 * May SEND an invoice and take it back to a draft ("Revise & Re-send"): `POST /invoices/{id}/send`,
 * `/mark-shared` and `/reset-to-draft` are all hasAnyRole(OWNER, ADMIN, BOOKKEEPER, CREW) — unlike
 * contracts (`canSendDocuments`), the Bookkeeper's whole job includes sending invoices, and the
 * access matrix says so for all six profiles. Kept as one named function so a decision to narrow it
 * is one edit here plus the two matrix rows; `InvoiceRevisePerProfile.test.tsx` fails until the
 * page and the matrix agree again (RB-28 item 7).
 */
export const canSendInvoices = (mine: readonly string[] | null | undefined) =>
  isOwner(mine) || isAdmin(mine) || isCrew(mine) || isBookkeeper(mine)

/** May open the studio-wide Finance page: revenue, VAT, reports, expenses. Never a bare column read. */
export const canOpenFinance = (mine: readonly string[] | null | undefined) =>
  hasAuthority(mine, AUTH.CAN_OPEN_FINANCE)

/** Sees only the projects they are assigned to — and those projects' galleries. */
export const scopeAssignedOnly = (mine: readonly string[] | null | undefined) =>
  hasAuthority(mine, AUTH.SCOPE_ASSIGNED_ONLY)

/**
 * The authority set a membership with these axes would carry — the mirror of
 * `AccessAuthorities.of(level, seesFinances, scopeAssignedOnly)`.
 *
 * Used by the invite presets (what will this person actually get?) and by the route
 * tests, which drive the six ADR-036 profiles rather than bare level names. A studio
 * never sees this function's output; it exists so the SPA can answer "what would
 * happen" without inventing a second version of the server's sentence.
 */
export function authoritiesFor(
  level: AccessLevel,
  opts: { seesFinances?: boolean; scopeAssignedOnly?: boolean } = {},
): string[] {
  const out: string[] = [`ROLE_${level}`]
  if (level === 'OWNER' || level === 'BOOKKEEPER' || (level === 'ADMIN' && !!opts.seesFinances)) {
    out.push(AUTH.CAN_OPEN_FINANCE)
  }
  if (level === 'CREW' && opts.scopeAssignedOnly) {
    out.push(AUTH.SCOPE_ASSIGNED_ONLY)
  }
  return out
}
